import Parse from 'parse';

import { Injectable } from '@angular/core';

import {
  AssetTopology,
  ParseBaseRepository,
  SampleAssetTopology,
  SampleTopology,
} from '@telespot/infrastructure/parse';

import { apiUrl } from '@telespot/shared/environment';
import { AssetState, IAssetItem } from '@analysis/domain';
import { type SampleAssetRepository } from '@analysis/use-cases';

@Injectable({
  providedIn: 'root',
})
export class TelespotSampleAssetService extends ParseBaseRepository implements SampleAssetRepository {
  private readonly limit = 25;

  constructor() {
    super(Parse, { useMasterKey: false });
  }

  public async listSampleAssets(sampleId: string) {
    const Sample = Parse.Object.extend(SampleTopology.TABLE);

    const sampleAssets = [];
    let skip = 0;
    let hasMore = true;
    let globalIndex = 0;

    while (hasMore) {
      const pageQuery = new Parse.Query(SampleAssetTopology.TABLE)
        .equalTo(SampleAssetTopology.SAMPLE, Sample.createWithoutData(sampleId))
        .include([
          SampleAssetTopology.SAMPLE,
          `${SampleAssetTopology.SAMPLE}.${SampleTopology.CASE}`,
          SampleAssetTopology.ASSET,
        ])
        .ascending([SampleAssetTopology.INDEX, SampleAssetTopology.DATE_OF_CAPTURE])
        .limit(this.limit)
        .skip(skip);

      const items = await pageQuery.find(this.options);

      if (items.length === 0 || items.length < this.limit) {
        hasMore = false;
      }

      if (items.length > 0) {
        const mappedItems = items.map((as, pageIndex) => {
          const assetItem = this.toAssetItem(as, globalIndex + pageIndex);
          return assetItem;
        });
        sampleAssets.push(...mappedItems);
        globalIndex += items.length;
        skip += items.length;
      }
    }

    return sampleAssets;
  }

  private toAssetItem(obj: Parse.Object, index: number): IAssetItem {
    const asset = obj.get(SampleAssetTopology.ASSET);
    const sample = obj.get(SampleAssetTopology.SAMPLE);
    const caseObj = sample?.get(SampleTopology.CASE);
    const assetId = asset.id;
    const assetFile = asset.get(AssetTopology.ASSET_FILE);
    const data = asset.get(AssetTopology.DATA);
    const hspan = data?.hSpan;
    const acquiredBy = asset.get(AssetTopology.CREATED_BY).id;
    const fav = asset.get(AssetTopology.FAVORITE) || false;

    const state = data?.width && data?.height ? AssetState.SUCEEDED : AssetState.PROCESSING;

    return {
      id: assetId,
      assetFile,
      url: this.getAssetUrl(assetFile),
      thumbnail: this.getThumbnail(assetFile),
      index,
      hspan,
      state,
      data,
      acquiredBy,
      fav,
      caseId: caseObj?.id,
      sampleId: sample?.id,
    };
  }

  private getThumbnail(fileName) {
    const imageRegex = /.(png|bmp|jpg|jpeg|dcm)$/gim;
    const videoRegex = /.(mp4|mov|avi|mpeg)$/gim;
    const assetFile = fileName.replace(/\.[^/.]+$/, '');
    return imageRegex.test(fileName)
      ? `${apiUrl('pyramids')}/${assetFile}/thumbnail`
      : videoRegex.test(fileName)
      ? `${apiUrl('videos')}/${assetFile}/thumbnail`
      : `@app/assets/media/image_placeholder.svg`;
  }

  private getAssetUrl(assetFile: string) {
    return assetFile ? encodeURI(`${apiUrl()}/files/${assetFile}`) : undefined;
  }
}
